import React, { useState, useEffect } from 'react'
import { useDesignSystems } from './hooks/useDesignSystems'
import { Header } from './components/Header'
import { Sidebar } from './components/Sidebar'
import { Preview } from './components/Preview'
import { CreateSystemModal } from './components/CreateSystemModal'
import { ExportModal } from './components/ExportModal'
import { MotifId } from './types'

export default function App() {
  const {
    systems,
    activeSystem,
    activeSystemId,
    createSystem,
    deleteSystem,
    renameSystem,
    setActiveSystem,
    updateToken,
    updateTypography,
    duplicateSystem,
    resetToMotif,
    hasNoSystems,
  } = useDesignSystems()

  const [showCreateModal, setShowCreateModal] = useState(false)
  const [showExportModal, setShowExportModal] = useState(false)

  // Show create modal on first load if no systems exist
  useEffect(() => {
    if (hasNoSystems) {
      setShowCreateModal(true)
    }
  }, [hasNoSystems])

  const handleCreateSystem = (name: string, motifId: MotifId) => {
    createSystem(name, motifId)
  }

  return (
    <div className="h-screen flex flex-col bg-gray-50">
      {/* Header */}
      <Header
        systems={systems}
        activeSystemId={activeSystemId}
        onSystemSelect={setActiveSystem}
        onCreateNew={() => setShowCreateModal(true)}
        onExport={() => setShowExportModal(true)}
        onDelete={deleteSystem}
        onDuplicate={duplicateSystem}
      />

      {/* Main Content */}
      <div className="flex-1 flex overflow-hidden">
        {activeSystem ? (
          <>
            {/* Sidebar */}
            <Sidebar
              tokens={activeSystem.tokens}
              systemName={activeSystem.name}
              onUpdateToken={updateToken}
              onUpdateTypography={updateTypography}
              onResetToMotif={resetToMotif}
              onRenameSystem={(name) => renameSystem(activeSystem.id, name)}
            />

            {/* Preview Area */}
            <main className="flex-1 flex flex-col overflow-hidden">
              <Preview tokens={activeSystem.tokens} />
            </main>
          </>
        ) : (
          <div className="flex-1 flex items-center justify-center">
            <div className="text-center">
              <div className="w-16 h-16 mx-auto mb-4 bg-gray-100 rounded-full flex items-center justify-center">
                <svg className="w-8 h-8 text-gray-400" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={1.5} d="M7 21a4 4 0 01-4-4V5a2 2 0 012-2h4a2 2 0 012 2v12a4 4 0 01-4 4zm0 0h12a2 2 0 002-2v-4a2 2 0 00-2-2h-2.343M11 7.343l1.657-1.657a2 2 0 012.828 0l2.829 2.829a2 2 0 010 2.828l-8.486 8.485M7 17h.01" />
                </svg>
              </div>
              <h2 className="text-lg font-medium text-gray-900 mb-2">No Design System</h2>
              <p className="text-sm text-gray-500 mb-4">
                Create your first design system to get started
              </p>
              <button
                onClick={() => setShowCreateModal(true)}
                className="px-4 py-2 text-sm font-medium text-white bg-gray-900 rounded-lg hover:bg-gray-800 transition-colors cursor-pointer"
              >
                Create Design System
              </button>
            </div>
          </div>
        )}
      </div>

      {/* Footer */}
      <footer className="border-t border-gray-200 bg-white">
        <div className="px-4 sm:px-6 lg:px-8 py-3">
          <p className="text-xs text-gray-500 text-center">
            Built by{' '}
            <a href="/" className="text-gray-700 hover:text-gray-900 underline cursor-pointer">
              Nimble Labs
            </a>
          </p>
        </div>
      </footer>

      {/* Modals */}
      <CreateSystemModal
        isOpen={showCreateModal}
        onClose={() => setShowCreateModal(false)}
        onCreate={handleCreateSystem}
      />

      {activeSystem && (
        <ExportModal
          isOpen={showExportModal}
          onClose={() => setShowExportModal(false)}
          tokens={activeSystem.tokens}
          systemName={activeSystem.name}
        />
      )}
    </div>
  )
}
